import { Button } from "@rothoblaas/design-system";
import { FC, memo, useContext, useState } from "react";
import { useFormContext } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import { ConcreteResinFormValues } from "../../../http/concreteResin/types/calculations";
import { useCalculationEntriesApi } from "../../../http/concreteResin/useCalculationEntries";
import styles from "./SaveCalculationEntryButton.module.css";

type SaveCalculationEntryButtonProps = {
  calculationId: string;
  /** Resolves once the server has the form's values, which the entry is copied from */
  waitForSave: () => Promise<void>;
  /** Disables it, e.g. until the calculation's settings are loaded */
  disabled?: boolean;
};

// Saves the current inputs and results as a new entry in the "saved calculations" card.
const SaveCalculationEntryButton: FC<SaveCalculationEntryButtonProps> = ({
  calculationId,
  waitForSave,
  disabled = false,
}) => {
  const i18n = useI18n();
  const api = useCalculationEntriesApi();
  const { trigger } = useFormContext<ConcreteResinFormValues>();
  const dispatch = useContext(NotificationDispatch);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    // A missing or invalid field shows its error, and focuses the first one, instead of saving an
    // incomplete entry. The errors clear as the fields get filled (the form validates on change).
    if (!(await trigger(undefined, { shouldFocus: true }))) return;
    setIsCreating(true);
    try {
      // A failed save is reported by the autosave already: no entry is copied from the previous values.
      const isSaved = await waitForSave().then(
        () => true,
        () => false,
      );
      if (!isSaved) return;
      await api.createEntry(calculationId);
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    // same primary, accent-colored action button as the deck designer's
    <Button
      type="button"
      variant="primary"
      color="var(--accent-base, var(--red))"
      className={styles.saveButton}
      onClick={handleCreate}
      isLoading={isCreating}
      disabled={disabled || isCreating}
    >
      {i18n.translation.concreteResinModule.addCalculation}
    </Button>
  );
};

export default memo(SaveCalculationEntryButton);
